// ContactNewPage — форма создания контакта (R17a, D14).
// После успешного create → редирект на /crm/contacts/{id}.
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { contactsApi } from '@/api/contacts';
import { useCompanyMap } from '@/api/companies';
import { useAuth } from '@/auth/AuthContext';
import { UserAutocomplete } from '@/components/UserAutocomplete';

/** Zod-схема валидации формы создания контакта. */
const contactFormSchema = z.object({
  firstName: z.string().min(1, 'Имя обязательно').max(100, 'Слишком длинное имя'),
  lastName: z.string().min(1, 'Фамилия обязательна').max(100, 'Слишком длинная фамилия'),
  position: z.string().max(100, 'Слишком длинная должность').optional().or(z.literal('')),
  email: z
    .string()
    .regex(/^$|^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, 'Некорректный email')
    .optional()
    .or(z.literal('')),
  phone: z
    .string()
    .regex(/^$|^[+]?[0-9 ()-]{5,50}$/, 'Некорректный телефон')
    .optional()
    .or(z.literal('')),
  companyId: z.string().optional().or(z.literal('')),
  ownerUserId: z.string().min(1, 'Владелец обязателен'),
  notes: z.string().max(5000, 'Слишком длинные заметки').optional().or(z.literal('')),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

/** Страница создания контакта. */
export function ContactNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  // R26 / баг D26: справочник компаний — shared hook R23 (size=100; 500 → HTTP 500 на backend).
  const companyMap = useCompanyMap();

  const { control, handleSubmit } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      companyId: '',
      ownerUserId: currentUser?.id ?? '',
      notes: '',
    },
  });

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: ContactFormValues) =>
      contactsApi.create({
        firstName: values.firstName,
        lastName: values.lastName,
        position: values.position || null,
        email: values.email || null,
        phone: values.phone || null,
        companyId: values.companyId || null,
        notes: values.notes || null,
        ownerUserId: values.ownerUserId,
        // Backend derive'ит departmentId из текущего пользователя; поле требуется TS-контрактом.
        departmentId: currentUser?.departments[0]?.id ?? '',
      }),
    onSuccess: (contact) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // U-29: селекторы построены на ['contacts-map'] — инвалидируем обе ветки (иначе no-op + staleTime).
      void queryClient.invalidateQueries({ queryKey: ['contacts-map'] });
      navigate(`/crm/contacts/${contact.id}`);
    },
    // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const onSubmit: SubmitHandler<ContactFormValues> = (values) => {
    if (submittingRef.current || mutation.isPending) return;
    submittingRef.current = true;
    mutation.mutate(values);
  };

  return (
    <Box>
      <PageHeader title="Новый контакт" subtitle="Заполните обязательные поля и сохраните" />
      {mutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mutation.error instanceof Error ? mutation.error.message : 'Не удалось создать контакт'}
        </Alert>
      ) : null}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Имя"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Имя' }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Фамилия"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Фамилия' }}
              />
            )}
          />
          <Controller
            name="position"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Должность"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Должность' }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Email контакта' }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Телефон"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Телефон контакта' }}
              />
            )}
          />
          <Controller
            name="companyId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Компания"
                inputProps={{ 'aria-label': 'Компания' }}
              >
                <MenuItem value="">—</MenuItem>
                {[...(companyMap ?? [])].map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {/* R36b / U-2: живой поиск владельца (OwnerUserSelect удалён). */}
          <Controller
            name="ownerUserId"
            control={control}
            render={({ field, fieldState }) => (
              <UserAutocomplete
                label="Владелец"
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                aria-label="Владелец контакта"
              />
            )}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Заметки"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Заметки контакта' }}
              />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={mutation.isPending}>
              Создать контакт
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
